import { useRecoilCallback } from "recoil";
import { LegalEntitiesState, legalEntitiesState } from "../store/legalEntities.store";
import { Entity, FolderNode, FolderTree } from "../../api-client-nswag/custompermission-client";
import { CountryGroupFolderName, ScheduleCustomPermissionStatusesEnum } from "../../constants";

export const useLegalEntitiesActions = () => {

  const loadFromApi = useRecoilCallback(
    ({ set }) =>
      (payload: { Entities: any[]; UsersPerSite: any[], folderPermission: any[] }) => {
        set(legalEntitiesState,
          (prev) => ({
            ...prev,
            entities: payload.Entities,
            usersPerSite: payload.UsersPerSite,
            snapshotEntities: payload.Entities,
            folderPermission: payload.folderPermission,
            folderPermissionSnapshot: payload.folderPermission,
          })
        );
      },
    [],
  );

  const loadFromRefreshFolderPermissions = useRecoilCallback(
    ({ set }) =>
      (payload: { Entities: Entity[]; folderPermission?: FolderTree; preservedEntities?: Entity[] }) => {
        set(legalEntitiesState, (prev) => {
          const allUsers = prev.usersPerSite.map((user) => String(user.userId));
          const preservedEntities = new Map(
            (payload.preservedEntities ?? []).map((entity) => [entity.entityId, entity])
          );
          const normalizeUnrestrictedFolders = (tree: FolderTree): FolderTree =>
            tree.map((node) => {
              const children = normalizeUnrestrictedFolders(node.children ?? []);
              return node.isRestricted === false
                ? {
                  ...node,
                  users: allUsers,
                  usersCount: allUsers.length,
                  children,
                }
                : { ...node, children };
            });

          return {
            ...prev,
            entities: payload.Entities.map((entity) => {
              const preservedEntity = preservedEntities.get(entity.entityId);
              return {
                ...entity,
                ...(entity.isRestricted === false
                  ? { userList: allUsers, numberOfUsers: allUsers.length }
                  : {}),
                ...(preservedEntity
                  ? {
                    isRestricted: preservedEntity.isRestricted,
                    userList: preservedEntity.userList,
                    numberOfUsers: preservedEntity.numberOfUsers,
                  }
                  : {}),
                folderTree: normalizeUnrestrictedFolders(entity.folderTree ?? []),
              };
            }),

            ...(payload.folderPermission !== undefined
              ? { folderPermission: normalizeUnrestrictedFolders(payload.folderPermission), }
              : {}),
          };
        });
      },
    [],
  );

  const updateIsButtonDisabled = useRecoilCallback(
    ({ set }) =>
      (payload: { status: string | null }) => {
        const allowedStatuses = [
          String(ScheduleCustomPermissionStatusesEnum.Cancelled),
          String(ScheduleCustomPermissionStatusesEnum.Verified),
          String(ScheduleCustomPermissionStatusesEnum.NotScheduled),
          null,
        ];
        const statusString = payload.status;
        const isDisabled = !allowedStatuses.includes(statusString);
        set(legalEntitiesState, (prev) => ({
          ...prev,
          isButtonDisabled: isDisabled,
        }));
      },
    [],
  );

  const updateFolderPermission = useRecoilCallback(
    ({ set }) =>
      (payload: { folderPermission: FolderNode; usersPerSite?: any[]; isCSFlow?: boolean }) => {
        const root = payload?.folderPermission;
        if (!root) return;

        set(legalEntitiesState, (prev: LegalEntitiesState) => {
          const effectiveUsersPerSite = Array.isArray(payload.usersPerSite)
            ? payload.usersPerSite
            : prev.usersPerSite;

          const allUsers: string[] = Array.isArray(effectiveUsersPerSite)
            ? effectiveUsersPerSite.map((u) => String(u.userId))
            : [];

          const siteUserIdSet = new Set(allUsers.map(String));
          const filterToSiteUsers = (ids: any): string[] => {
            const arr = Array.isArray(ids) ? ids : [];
            return arr.map(String).filter(id => siteUserIdSet.has(String(id)))
          };

          const hasAllSiteUsers = (users: Array<string | number> | undefined | null): boolean => {
            const filteredUsers = filterToSiteUsers(users);
            const userSet = new Set(filteredUsers.map(String));

            return (
              userSet.size === siteUserIdSet.size &&
              allUsers.every(userId => userSet.has(userId))
            );
          };

          const entityIndex = new Map<number, Entity>(
            Array.isArray(prev.entities)
              ? prev.entities.map((e) => [e.entityId, e])
              : []
          );

          const findMaxCountryGroupFolderCount = (node: FolderNode): number => {
            let maxCount = 0;

            const isCountryGroupName = CountryGroupFolderName.some(name => node?.folderName?.toLowerCase().includes(name.toLowerCase()));

            if (isCountryGroupName) {
              const childrenCount = Array.isArray(node.children) ? node.children.length : 0;
              maxCount = childrenCount;
            }

            for (const child of node.children ?? []) {
              maxCount = Math.max(maxCount, findMaxCountryGroupFolderCount(child));
            }

            return maxCount;
          };
          const countryGroupFolderCount = payload?.isCSFlow ? findMaxCountryGroupFolderCount(root) : 0;
          const isCountryGroupRoot = (node: FolderNode): boolean => {
            if (!payload?.isCSFlow) {
              return false;
            }

            const folderNameMatches = CountryGroupFolderName.some((name) => node?.folderName?.toLowerCase().includes(name.toLowerCase()));

            if (!folderNameMatches) {
              return false;
            }
            const childrenCount = Array.isArray(node.children) ? node.children.length : 0;

            return (childrenCount > 0 && childrenCount === countryGroupFolderCount);
          };

          const cloneFolderTreeForEntity = (node: FolderNode, parentIsCountryGroup = false): FolderNode => {
            const requestedEntityId = Number(node.entityId);
            const hasEntityId = Number.isFinite(requestedEntityId);
            const owner = requestedEntityId !== 0 ? entityIndex.get(requestedEntityId) : undefined;
            const normalizedEntityId = hasEntityId && (requestedEntityId === 0 || owner) ? requestedEntityId : 0;
            const isCustomFolder = normalizedEntityId === 0;
            const isCurrentCountryGroup = isCountryGroupRoot(node);
            const isCountryGroup = parentIsCountryGroup || isCurrentCountryGroup;
            const allUsersSet = new Set(allUsers);
            const filteredNodeUsers = (node.users ?? []).filter(user => allUsersSet.has(String(user)));
            const shouldOverride = (isCustomFolder || (!!owner && owner.isRestricted === false)) && allUsers.length > 0;
            const nextChildren = node.children?.map(child => cloneFolderTreeForEntity(child, isCountryGroup)) ?? [];
            let users = shouldOverride ? allUsers : filteredNodeUsers;
            let usersCount = shouldOverride ? allUsers.length : filteredNodeUsers.length;
            let isRestricted = node.isRestricted;

            if (isCustomFolder) {
              const hasNodeUsers = filteredNodeUsers.length > 0;
              const isFullAccess = node.usersCount === allUsers.length;

              if ((!hasNodeUsers || usersCount === 0) && node.isRestricted !== true) {
                users = allUsers;
                usersCount = allUsers.length;
                isRestricted = false;
              } else if (hasNodeUsers && isFullAccess) {
                isRestricted = node.isRestricted ?? false;
              } else {
                users = filteredNodeUsers;
                usersCount = filteredNodeUsers.length;
                isRestricted = true;
              }
            }

            return {
              ...node,
              entityId: normalizedEntityId,
              users,
              usersCount,
              isRestricted: isCustomFolder ? isRestricted : node.isRestricted,
              isCountryGroup,
              children: nextChildren,
            };
          };

          const inheritFromParent = (node: FolderNode, parent?: FolderNode): FolderNode => {
            const nodeUsers = filterToSiteUsers(node?.users);
            const parentUsers = filterToSiteUsers(parent?.users);
            const nodeUsersCount = nodeUsers.length;
            const parentUsersCount = parentUsers.length;
            const parentRestricted = parent?.isRestricted === true;
            const isNewFolder = node?.isRestricted === false && nodeUsersCount === 0;
            let effectiveUsers = nodeUsers;
            let effectiveUsersCount = nodeUsersCount;
            let effectiveIsRestricted = node?.isRestricted;

            if (isNewFolder && parent && parent?.parentFolderId !== 0) {
              if (parentUsersCount > 0) {
                effectiveUsers = [...parentUsers];
                effectiveUsersCount = parentUsersCount;
              }
              effectiveIsRestricted = parentRestricted;
            }

            const nextChildren = (node.children ?? []).map(child =>
              inheritFromParent(child, {
                ...node,
                users: effectiveUsers,
                usersCount: effectiveUsersCount,
                isRestricted:
                  effectiveIsRestricted,
              } as FolderNode)
            );

            return {
              ...node,
              users: effectiveUsers,
              usersCount: effectiveUsersCount,
              isRestricted: effectiveIsRestricted,
              children: nextChildren,
            };
          };

          const updatedSiteTree = inheritFromParent(cloneFolderTreeForEntity(root));
          /*
           * This only checks whether the entity contains a
           * restricted folder.
           *
           * It does not modify the parent or sibling folders.
           */
          const hasRestrictedFolder = (nodes: FolderNode[] | undefined): boolean => {
            return (nodes ?? []).some(node => {
              if (node.isRestricted === true) {
                return true;
              }
              return hasRestrictedFolder(node.children);
            });
          };

          const updateEntityTree = (entityList: Entity[]): Entity[] => {
            return entityList.map(entity => {
              const filteredEntityUserList = filterToSiteUsers(entity?.userList ?? []);

              const newEntity: Entity =
                !entity.isRestricted
                  ? {
                    ...entity,
                    userList: allUsers,
                    numberOfUsers: allUsers.length,
                    folderTree: entity.folderTree ?? undefined,
                  }
                  : {
                    ...entity,
                    userList: filteredEntityUserList,
                    numberOfUsers: filteredEntityUserList.length,
                  };

              const entityRootNode = updatedSiteTree.children?.find(child => child.entityId === entity.entityId);
              if (!entityRootNode) {
                return newEntity;
              }

              const updatedFolderTreeChildren = entityRootNode.children?.map(child => cloneFolderTreeForEntity(child, entityRootNode.isCountryGroup === true)) ?? [];

              const nextFolderRoot: FolderNode = {
                ...entityRootNode,
                children: updatedFolderTreeChildren,
              };

              /*
               * Entity summary status:
               *
               * 1. Restricted when its own user list is incomplete.
               * 2. Restricted when any folder inside it is restricted.
               *
               * This does not change the restriction status of
               * parent folders or siblings.
               */
              const entityUsersRestricted = !hasAllSiteUsers(newEntity.userList);
              const entityHasRestrictedFolder = hasRestrictedFolder([nextFolderRoot,]);
              const entityRestricted = entityUsersRestricted || entityHasRestrictedFolder;

              return {
                ...newEntity,
                isRestricted: entityRestricted,
                folderTree: [nextFolderRoot],
              };
            });
          };

          return {
            ...prev,
            folderPermission: [updatedSiteTree,],
            folderPermissionSnapshot: [updatedSiteTree,],
            entities: updateEntityTree(prev.entities),
            snapshotEntities: updateEntityTree(prev.snapshotEntities),
          };
        });
      },
    []
  );

  const updateEntityUserList = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newUserList: string[]) => {
        set(legalEntitiesState, (prev) => {
          const entities: Entity[] = prev?.entities ?? [];

          const nextEntities = entities.map((e) => {
            if (e.entityId !== entityId) return e;

            const prevUserList = e.userList ?? [];
            const newSet = new Set(newUserList);

            const removed = prevUserList.filter((u) => !newSet.has(u));
            const toRemove = new Set(removed);

            const addedUsers = newUserList.filter((u) => !prevUserList.includes(u));
            const toAdd = new Set(addedUsers);

            const reconcileNode = (node: FolderNode): FolderNode => {
              const startingUsers = node.users ?? [];

              let users = toRemove.size
                ? startingUsers.filter((u) => !toRemove.has(u))
                : startingUsers;

              if (toAdd.size) {
                const additions = Array.from(toAdd).filter((u) => !users.includes(u));
                if (additions.length) {
                  users = [...users, ...additions];
                }
              }

              const children = (node.children ?? []).map(reconcileNode);
              return {
                ...node,
                users,
                usersCount: users.length,
                children,
              };
            };

            const folderTree = e.folderTree ?? [];
            let nextFolderTree: FolderNode[] = folderTree;

            if (toRemove.size > 0 || toAdd.size > 0) {
              nextFolderTree = folderTree.map(reconcileNode);
            }

            return {
              ...e,
              userList: newUserList,
              numberOfUsers: newUserList.length,
              isRestricted: e.isRestricted,
              folderTree: nextFolderTree,
            };
          });

          return { ...prev, entities: nextEntities };
        });
      },
    []
  );

  const updateEntityUserListFolderPermission = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newUserList: string[]) => {
        set(legalEntitiesState, (prev) => {

          const folderPermission: FolderTree = prev?.folderPermission ?? [];
          if (!folderPermission.length) {
            return prev;
          }

          const parent = folderPermission[0];
          const parentChildren = parent?.children ?? [];

          const nextChildren = parentChildren.map((e) => {
            if (e.entityId !== entityId) return e;

            const prevUserList = e.users ?? [];
            const newSet = new Set(newUserList);

            const removed = prevUserList.filter((u) => !newSet.has(u));
            const toRemove = new Set(removed);

            const addedUsers = newUserList.filter((u) => !prevUserList.includes(u));
            const toAdd = new Set(addedUsers);

            const reconcileNode = (node: FolderNode): FolderNode => {
              const startingUsers = node.users ?? [];

              let users = toRemove.size
                ? startingUsers.filter((u) => !toRemove.has(u))
                : startingUsers;

              if (toAdd.size) {
                const additions = Array.from(toAdd).filter((u) => !users.includes(u));
                if (additions.length) {
                  users = [...users, ...additions];
                }
              }

              const children = (node.children ?? []).map(reconcileNode);

              return {
                ...node,
                users,
                usersCount: users.length,
                children,
              };
            };

            const folderTree = e.children ?? [];
            const nextFolderTree =
              toRemove.size > 0 || toAdd.size > 0
                ? folderTree.map(reconcileNode)
                : folderTree;

            return {
              ...e,
              users: newUserList,
              usersCount: newUserList.length,
              children: nextFolderTree,
            };
          });

          const nextFolderPermission = [...folderPermission];
          nextFolderPermission[0] = {
            ...parent,
            children: nextChildren,
          };

          return {
            ...prev,
            folderPermission: nextFolderPermission,
          };
        });
      },
    []
  );

  const updateEntityFolderTree = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newFolderTree: FolderTree) => {
        set(legalEntitiesState, (prev) => {
          const updatedEntities = prev.entities.map((e) =>
            e.entityId === entityId ? { ...e, folderTree: newFolderTree } : e
          );

          const updatedFolderPermission = (prev.folderPermission ?? []).map((siteNode) => {
            if (!siteNode.children?.length) return siteNode;

            const updatedChildren = siteNode.children.map((childNode) => {
              if (childNode.entityId === entityId) {
                return { ...newFolderTree[0] };
              }
              return childNode;
            });

            return { ...siteNode, children: updatedChildren };
          });

          return {
            ...prev,
            entities: updatedEntities,
            folderPermission: updatedFolderPermission,
          };
        });
      },
    []
  );

  const updateSiteFolderPermission = useRecoilCallback(
    ({ set }) =>
      (updatedSiteTree: FolderTree) => {
        set(legalEntitiesState, (prev) => {
          if (!Array.isArray(updatedSiteTree)) return prev;

          const annotate = (nodes: FolderNode[]): FolderNode[] =>
            nodes.map((node) => {
              const children = node.children?.length ? annotate(node.children) : [];
              if (node.entityId === 0) {
                return {
                  ...node,
                  children,
                };
              }

              return children === node.children ? node : { ...node, children };
            });

          const nextFolderPermission: FolderTree = annotate(updatedSiteTree);

          const findEntityNode = (nodes: FolderNode[], entityId: number): FolderNode | null => {
            for (const n of nodes) {
              if (n.entityId === entityId) return n;
              if (n.children?.length) {
                const found = findEntityNode(n.children, entityId);
                if (found) return found;
              }
            }
            return null;
          };

          const updatedEntities = (prev?.entities ?? []).map((e) => {
            const node = findEntityNode(nextFolderPermission, e.entityId);
            return node ? { ...e, folderTree: [node] } : e;
          });

          return {
            ...prev,
            folderPermission: nextFolderPermission,
            entities: updatedEntities,
          };
        });
      },
    []
  );

  const resetEntitiesToSnapshot = useRecoilCallback(
    ({ set }) =>
      () => {
        set(legalEntitiesState, (prev) => ({
          ...prev,
          entities: prev.snapshotEntities,
          folderPermission: prev.folderPermissionSnapshot,
        }));
      },
    [],
  );

  const resetEntitiesState = useRecoilCallback(
    ({ set }) =>
      () => {
        set(legalEntitiesState, (prev) => ({
          ...prev,
          entities: [],
          snapshotEntities: [],
          folderPermission: [],
          folderPermissionSnapshot: [],
          usersPerSite: [],
        }));
      },
    [],
  );

  const revalidateEntityIsRestricted = useRecoilCallback(
    ({ set, snapshot }) =>
      async (entityId: number) => {
        const entitiesState = await snapshot.getPromise(legalEntitiesState);
        const entities: Entity[] = entitiesState?.entities ?? [];
        const folderPermission: FolderTree = entitiesState?.folderPermission ?? [];
        const allUsers: string[] = (entitiesState?.usersPerSite ?? []).map(user => String(user.userId));

        const hasAllSiteUsers = (users: Array<string | number> | null | undefined, allUsers: string[]): boolean => {
          const folderUserSet = new Set((users ?? []).map(String));
          const siteUserSet = new Set(allUsers);

          return (
            folderUserSet.size === siteUserSet.size &&
            allUsers.every(userId => folderUserSet.has(userId))
          );
        };

        const getUniqueUserCount = (users: Array<string | number> | null | undefined): number => new Set((users ?? []).map(String)).size;

        const updateFolderTree = (tree: FolderTree, allUsers: string[], parentRestricted = false): FolderTree => (tree ?? []).map(node => {
          const isRestricted = parentRestricted || !hasAllSiteUsers(node.users, allUsers);

          return {
            ...node,
            usersCount: getUniqueUserCount(node.users),
            isRestricted,
            children: updateFolderTree(node.children ?? [], allUsers, isRestricted),
          };
        });


        const hasRestrictedFolder = (tree: FolderTree): boolean => (tree ?? []).some(node =>
          node.isRestricted ||
          hasRestrictedFolder(node.children ?? [])
        );

        let updatedEntity: Entity | undefined;

        const nextEntities = entities.map(entity => {
          if (Number(entity.entityId) !== Number(entityId)) {
            return entity;
          }

          const nextFolderTree = updateFolderTree(entity.folderTree ?? [], allUsers);

          const entityRestricted = hasRestrictedFolder(nextFolderTree);

          const nextEntity: Entity = {
            ...entity,
            isRestricted: entityRestricted,
            folderTree: nextFolderTree,
          };

          updatedEntity = nextEntity;

          return nextEntity;
        });

        const rootFolder = folderPermission[0];

        const nextFolderPermission: FolderTree =
          rootFolder
            ? [
              {
                ...rootFolder,
                children: (rootFolder.children ?? []).map(node => {
                  if (Number(node.entityId) === 0) {
                    return updateFolderTree([node], allUsers)[0];
                  }

                  if (Number(node.entityId) !== Number(entityId)) {
                    return node;
                  }

                  const updatedEntityRoot = updatedEntity?.folderTree?.[0];

                  if (!updatedEntityRoot) {
                    return node;
                  }

                  return {
                    ...node,
                    ...updatedEntityRoot,
                  };
                }),
              },
            ]
            : folderPermission;

        set(legalEntitiesState, {
          ...entitiesState,
          entities: nextEntities,
          folderPermission: nextFolderPermission,
        });
      },
    []
  );

  const revalidateAllEntitiesIsRestricted = useRecoilCallback(
    ({ set, snapshot }) =>
      async () => {
        const entitiesState = await snapshot.getPromise(legalEntitiesState);
        const entities: Entity[] = entitiesState?.entities ?? [];
        const folderPermission: FolderTree = entitiesState?.folderPermission ?? [];
        const allUsers: string[] = (entitiesState?.usersPerSite ?? []).map(user => String(user.userId));

        const hasAllSiteUsers = (users: Array<string | number> | null | undefined, allUsers: string[]): boolean => {
          const folderUserSet = new Set((users ?? []).map(String));
          const siteUserSet = new Set(allUsers);

          return (
            folderUserSet.size === siteUserSet.size &&
            allUsers.every(userId => folderUserSet.has(userId))
          );
        };

        const getUniqueUserCount = (users: Array<string | number> | null | undefined): number => new Set((users ?? []).map(String)).size;

        const updateFolderTree = (tree: FolderTree, allUsers: string[], parentRestricted = false): FolderTree => (tree ?? []).map(node => {
          const isRestricted = parentRestricted || !hasAllSiteUsers(node.users, allUsers);

          return {
            ...node,
            usersCount: getUniqueUserCount(node.users),
            isRestricted,
            children: updateFolderTree(node.children ?? [], allUsers, isRestricted),
          };
        });

        const hasRestrictedFolder = (tree: FolderTree): boolean => (tree ?? []).some(node => node.isRestricted || hasRestrictedFolder(node.children ?? []));
        const nextEntities = entities.map(entity => {
          const nextFolderTree = updateFolderTree(entity.folderTree ?? [], allUsers);
          const entityRestricted = hasRestrictedFolder(nextFolderTree);

          return {
            ...entity,
            isRestricted: entityRestricted,
            folderTree: nextFolderTree,
          };
        });

        const entityMap = new Map(nextEntities.map(entity => [Number(entity.entityId), entity,]));
        const rootFolder = folderPermission[0];
        const nextFolderPermission: FolderTree =
          rootFolder
            ? [
              {
                ...rootFolder,
                children: (rootFolder.children ?? []).map(node => {
                  if (Number(node.entityId) === 0) {
                    return updateFolderTree([node], allUsers)[0];
                  }
                  const updatedEntity = entityMap.get(Number(node.entityId));
                  const updatedEntityRoot = updatedEntity?.folderTree?.[0];
                  if (!updatedEntityRoot) { return node; }

                  return {
                    ...node,
                    ...updatedEntityRoot,
                  };
                }),
              },
            ]
            : folderPermission;

        set(legalEntitiesState, {
          ...entitiesState,
          entities: nextEntities,
          folderPermission: nextFolderPermission,
        });
      },
    []
  );

  return {
    loadFromApi,
    updateIsButtonDisabled,
    updateFolderPermission,
    updateEntityUserList,
    updateEntityUserListFolderPermission,
    updateEntityFolderTree,
    updateSiteFolderPermission,
    revalidateEntityIsRestricted,
    revalidateAllEntitiesIsRestricted,
    resetEntitiesToSnapshot,
    resetEntitiesState,
    loadFromRefreshFolderPermissions
  };
};
